import { z } from "zod"
import { ENVIRONMENTS } from "./filters.js"

/**
 * ⚠ **This schema exists because of a real failure.** The frontend used to declare its own
 * `CollectionRun` type and **assert** the response matched it via `get<CollectionRun[]>()` —
 * while the endpoint actually returned the collector's raw `SELECT *`, in `snake_case`.
 * The declared type did not match the wire shape, TypeScript could not see a thing, and the
 * whole page died on `pointsWritten.toLocaleString()`.
 */

const count = z.coerce.number().int()

export const collectionRunSchema = z.object({
  id: z.string(),
  trigger: z.enum(["cron", "manual", "backfill"]),
  startedAt: z.iso.datetime({ offset: true }),
  finishedAt: z.iso.datetime({ offset: true }).nullable(),
  tenantsTotal: count,
  tasksTotal: count,
  tasksOk: count,
  tasksFailed: count,
  tasksSkipped: count,
  pointsWritten: count,
  /**
   * ⚠ **This column does not exist on `collection_run`** — it is aggregated from
   * `collection_task`.
   */
  pointsRejected: count,
  requestedBy: z.string().nullable(),
})
export type CollectionRun = z.infer<typeof collectionRunSchema>

export const collectionTaskSchema = z.object({
  tenantId: z.string(),
  /**
   * ⚠ Why they are nullable: `collection_task.tenant_id` has **no foreign key**
   * (0001_init.sql:175), because collection history must outlive the deployment itself.
   * After a deployment is deleted its history must still be readable — the name falls back
   * to the id and the environment is `null`, rather than the row vanishing or the endpoint 500ing.
   */
  displayName: z.string(),
  environment: z.enum(ENVIRONMENTS).nullable(),
  srvModule: z.string(),
  state: z.enum(["ok", "unsupported", "error", "skipped"]),
  since: z.iso.datetime({ offset: true }).nullable(),
  until: z.iso.datetime({ offset: true }).nullable(),
  httpStatus: count.nullable(),
  durationMs: count.nullable(),
  attempts: count,
  pointsIn: count,
  pointsWritten: count,
  pointsRejected: count,
  truncated: z.boolean(),
  errorCode: z.string().nullable(),
  errorMsg: z.string().nullable(),
})
export type CollectionTask = z.infer<typeof collectionTaskSchema>

export const runListSchema = z.object({ runs: z.array(collectionRunSchema) })
export type RunList = z.infer<typeof runListSchema>
export const runDetailSchema = z.object({
  run: collectionRunSchema,
  tasks: z.array(collectionTaskSchema),
})
export type RunDetail = z.infer<typeof runDetailSchema>

/**
 * ⚠ **There is no `tasksTotal`.** The frontend once hand-wrote
 * `{ runId?, tasksTotal?, tasksFailed? }`; that field never existed — nobody happened to read
 * it, so it stayed `undefined` instead of erroring. Task counts split into ok / failed /
 * skipped because "skipped" is not a failure (that deployment's srv does not support the
 * module), and adding them into one total erases exactly that distinction.
 */
export const runSummarySchema = z.object({
  runId: z.string(),
  tenants: z.number().int(),
  tasksOk: z.number().int(),
  tasksFailed: z.number().int(),
  tasksSkipped: z.number().int(),
  pointsWritten: z.number().int(),
  pointsRejected: z.number().int(),
  durationMs: z.number().int(),
})
export type RunSummary = z.infer<typeof runSummarySchema>

/**
 * ⚠ The module list is called **`availableModules`**, not `modules`. The frontend hand-wrote
 * the latter, so the N in "Reachable — … N/14 modules" was **always 0** — while srv was
 * reporting all 14. `host` and `srvVersion` happened to match by name, so those two were
 * real and only the module count was fake, which made it that much harder to suspect.
 */
export const probeResultSchema = z.object({
  tenantId: z.string(),
  host: z.string().nullable(),
  srvVersion: z.string().nullable(),
  availableModules: z.array(z.string()).nullable(),
  newModules: z.array(z.string()),
  upgradedFrom: z.string().nullable(),
})
export type ProbeResult = z.infer<typeof probeResultSchema>
